import { useCallback, useEffect, useReducer } from 'react';
import toast from 'react-hot-toast';
import { fetchClientApi, postClient, putClient } from '~/api';
import { FILL_REQUIRED_FIELDS } from '~/constants/errors';
import { selectUser } from '~/selectors';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { IClient } from '~/types/client';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { hasErrorFields } from '~/utils/utils';
import { useUserLimits } from '~/hooks/useUserLimits';

interface State {
  client: IClient;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

const initialState: State = {
  client: {
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    website: '',
    country_code: '',
    phone: '',
    currency: '',
    country: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
  },
  isLoading: false,
  isSaving: false,
  error: null,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: IClient }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'UPDATE_CLIENT'; payload: Partial<IClient> }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS' }
  | { type: 'SAVE_FAILURE'; error: string }
  | { type: 'RESET' };

const clientReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      return { ...state, isLoading: false, client: action.payload };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'UPDATE_CLIENT':
      return { ...state, client: { ...state.client, ...action.payload } };

    case 'SAVE_START':
      return { ...state, isSaving: true, error: null };

    case 'SAVE_SUCCESS':
      return { ...state, isSaving: false };

    case 'SAVE_FAILURE':
      return { ...state, isSaving: false, error: action.error };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};

export const useAddClient = (
  businessClientUuid: string | null,
  onClose: () => void,
  onClientCreated?: (client: IClient) => void,
) => {
  const [state, dispatch] = useReducer(clientReducer, initialState);
  const businessUuid = useSelector(selectBusinessUuid);
  const user = useSelector(selectUser);
  const businessCurrency = user.businessDetails.currency;
  const { refetch: refetchUserLimits } = useUserLimits(false);

  // Update form state
  const onChange = (updates: Partial<IClient>) => {
    dispatch({ type: 'UPDATE_CLIENT', payload: updates });
  };

  // Fetch existing client data if in edit mode
  const fetchClient = useCallback(async () => {
    if (!businessClientUuid) {
      onChange({ currency: businessCurrency });
      return;
    }

    dispatch({ type: 'FETCH_START' });

    const promise = await fetchClientApi(businessUuid, businessClientUuid);
    const response = await apiResponseHandler<IClient>(promise);

    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'FETCH_SUCCESS', payload: response.data });
    } else {
      dispatch({ type: 'FETCH_FAILURE', error: response.error });
    }
  }, [businessClientUuid]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  // Save client (POST for add, PUT for edit)
  const saveClient = async () => {
    if (hasErrorFields('clientFormContainer')) {
      toast.error(FILL_REQUIRED_FIELDS);
      return;
    }

    dispatch({ type: 'SAVE_START' });

    let promise: Promise<any>;
    if (businessClientUuid) {
      promise = await putClient(businessUuid, businessClientUuid, state.client);
    } else {
      promise = await postClient(businessUuid, state.client);
    }

    const response = await apiResponseHandler<IClient>(promise);

    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'SAVE_SUCCESS' });
      toast.success('Saved client details');

      // Invalidate user limits cache on new client creation
      if (!businessClientUuid) {
        refetchUserLimits();
      }

      // If creating a new client (not editing), call the callback with the created client data
      if (!businessClientUuid && onClientCreated) {
        onClientCreated(response.data);
      }

      onClose();
    } else {
      dispatch({ type: 'SAVE_FAILURE', error: response.error });
      toast.error(response.error ?? 'Error occurred in saving client details');
    }
  };

  return {
    ...state,
    onChange,
    saveClient,
    reset: () => dispatch({ type: 'RESET' }),
  };
};
